import Link from "next/link";
import { BrandMark } from "@/components/BrandMark";

type Lang = "en" | "es";

const T = {
  tagline: {
    en: "One camera, the whole pitch. Match video, real stats and a Passport for every player.",
    es: "Una cámara, toda la cancha. Video del partido, estadísticas reales y un Passport para cada jugador.",
  },
  product: { en: "Product", es: "Producto" },
  company: { en: "Company", es: "Compañía" },
  legal: { en: "Legal", es: "Legal" },
  rights: { en: "All rights reserved.", es: "Todos los derechos reservados." },
};

const PRODUCT = [
  { label: { en: "The camera", es: "La cámara" }, href: "/#camera" },
  { label: { en: "AI video", es: "Video con IA" }, href: "/#director" },
  { label: { en: "Passport", es: "Passport" }, href: "/#passport" },
  { label: { en: "For clubs", es: "Para clubes" }, href: "/#clubs" },
  { label: { en: "Pricing", es: "Precios" }, href: "/pricing" },
];

const COMPANY = [
  { label: { en: "Get started", es: "Empezar" }, href: "/signup" },
  { label: { en: "Sign in", es: "Iniciar sesión" }, href: "/signin" },
  { label: { en: "Contact", es: "Contacto" }, href: "/contact" },
];

const LEGAL = [
  { label: { en: "Terms & Conditions", es: "Términos y Condiciones" }, href: "/terms-and-conditions" },
  { label: { en: "Privacy Policy", es: "Política de Privacidad" }, href: "/privacy-policy" },
  { label: { en: "Cookie Policy", es: "Política de Cookies" }, href: "/cookie-policy" },
];

function Column({
  title,
  links,
  lang,
}: {
  title: string;
  links: { label: Record<Lang, string>; href: string }[];
  lang: Lang;
}) {
  return (
    <div>
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50">{title}</h3>
      <ul className="mt-4 space-y-2.5">
        {links.map((l) => (
          <li key={l.href + l.label.en}>
            <Link href={l.href} className="text-sm text-white/65 transition-colors hover:text-white">
              {l.label[lang]}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer({ lang = "en" }: { lang?: Lang }) {
  const year = new Date().getFullYear();
  return (
    <footer className="relative border-t border-white/10 bg-[#05070d]">
      <div className="mx-auto max-w-6xl px-6 py-14 sm:px-8 sm:py-16">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-5">
          <div className="col-span-2 md:col-span-2">
            <Link href="/" className="flex items-center gap-2.5" aria-label="Draffte">
              <BrandMark className="h-8 w-8 rounded-lg" />
              <span className="font-display text-lg font-semibold tracking-tight text-white">Draffte</span>
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-white/60">{T.tagline[lang]}</p>
            <p className="mt-5 text-xs text-zinc-600">Draffte Inc. · Delaware, USA</p>
          </div>

          <Column title={T.product[lang]} links={PRODUCT} lang={lang} />
          <Column title={T.company[lang]} links={COMPANY} lang={lang} />
          <Column title={T.legal[lang]} links={LEGAL} lang={lang} />
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center">
          <p className="text-xs text-zinc-500">© {year} Draffte Inc. {T.rights[lang]}</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {LEGAL.map((l) => (
              <Link key={l.href} href={l.href} className="text-xs text-zinc-500 transition-colors hover:text-zinc-300">
                {l.label[lang]}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
